import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';
import { dateTime } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { AboutThisPage, PageHeader, SectionCard, StatCard, StatGrid } from '../../ui/primitives';
import Composer from './Composer';
import ReportRunsTab from './ReportRunsTab';
import { templateFor, TEMPLATES } from './templates';

export default function CommunicationsPage() {
  const scope = useTenantScope();
  const [tab, setTab] = useState(0);
  const logs = scope.communications;
  const delivered = sumBy(logs, (log) => log.delivered);
  return (
    <>
      <PageHeader
        title="Communications"
        subtitle="Every notice, advice and message sent to holders, the templates behind them, and bulk messaging."
      />
      <AboutThisPage>
        <p>
          Registry events send their own communications automatically (call notices, advices, tax
          statements, wind-up notices). Quarterly reports go out as one approved run, with each
          investor&apos;s statement generated from the register instead of attached by hand. Each is
          logged here with delivery and open rates, and appears in the investor&apos;s inbox in the
          app. Holders who prefer post get a printed copy through the mail house.
        </p>
      </AboutThisPage>
      <StatGrid>
        <StatCard label="Sent (all time)" value={logs.length} hint={`${delivered} deliveries`} />
        <StatCard
          label="Open rate"
          value={
            delivered ? `${Math.round((sumBy(logs, (log) => log.opened) / delivered) * 100)}%` : '—'
          }
        />
        <StatCard
          label="Bounced"
          value={sumBy(logs, (log) => log.bounced)}
          tone={sumBy(logs, (log) => log.bounced) ? 'warning' : 'neutral'}
        />
      </StatGrid>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_event, next) => setTab(next)}>
          <Tab label="Quarterly reports" />
          <Tab label="Sent" />
          <Tab label="Templates" />
          <Tab label="One-off message" />
        </Tabs>
      </Box>
      {tab === 0 && <ReportRunsTab />}
      {tab === 1 && (
        <Paper variant="outlined">
          <DataTable
            rows={logs}
            rowKey={(row) => row.id}
            dense
            defaultSort={{ key: 'at', direction: 'desc' }}
            columns={[
              {
                key: 'at',
                label: 'Sent',
                sortValue: (row) => row.at,
                render: (row) => dateTime(row.at),
              },
              {
                key: 'subject',
                label: 'Message',
                render: (row) => (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.subject}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {templateFor(row.template).name} · {row.audience}
                    </Typography>
                  </>
                ),
              },
              {
                key: 'channels',
                label: 'Channels',
                hideBelow: 'md',
                render: (row) => row.channels.map((channel) => channel.toLowerCase()).join(', '),
              },
              {
                key: 'delivered',
                label: 'Delivered',
                align: 'right',
                render: (row) => row.delivered,
              },
              {
                key: 'opened',
                label: 'Opened',
                align: 'right',
                render: (row) =>
                  row.delivered ? `${Math.round((row.opened / row.delivered) * 100)}%` : '—',
              },
              { key: 'by', label: 'Sent by', hideBelow: 'lg', render: (row) => row.sentBy },
            ]}
          />
        </Paper>
      )}
      {tab === 2 && (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {TEMPLATES.map((template) => (
            <SectionCard key={template.key} title={template.name} subtitle={template.trigger}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {template.subject}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'pre-line', mt: 1 }}
              >
                {template.body}
              </Typography>
            </SectionCard>
          ))}
        </Box>
      )}
      {tab === 3 && <Composer onSent={() => setTab(1)} />}
    </>
  );
}
